import React, { useCallback, useEffect, useState } from "react";
import MerchantLayout from "@/components/MerchantLayout";
import TablePagination from "@/components/TablePagination";
import { mApi } from "@/lib/api";
import { ArrowClockwise } from "@phosphor-icons/react";

const PAGE_SIZE = 25;

export default function MerchantWebhookEvents() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filter) params.status = filter;
      const r = await mApi.get("/merchant/webhook-events", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [filter, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter, limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="merchant-webhook-events">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Observability</div>
            <h1 className="font-display text-3xl tracking-tight font-medium">Webhook deliveries</h1>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl">
              Every outbound webhook from Blonix to your endpoint, including
              attempt history and HMAC signing metadata.
            </p>
            <p className="text-sm text-zinc-500 mt-1 tabular-nums">{total} events</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="we-filter"
              className="border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white"
            >
              <option value="">All statuses</option>
              <option value="delivered">Delivered</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <button
              onClick={load}
              data-testid="we-refresh"
              className="inline-flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-2 text-sm bg-white hover:bg-zinc-50"
            >
              <ArrowClockwise size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm" data-testid="we-table-wrap">
          <div className="table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">
                <th className="text-left py-2.5 px-4">Event</th>
                <th className="text-left py-2.5 px-4">Attempts</th>
                <th className="text-left py-2.5 px-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="text-center text-zinc-500 py-12">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={3} className="text-center text-zinc-500 py-12">No webhook events yet.</td></tr>
              ) : items.map((ev) => (
                <tr
                  key={ev.id}
                  data-testid={`we-row-${ev.id}`}
                  onClick={() => setOpen(ev)}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                >
                  <td className="py-3 px-4 font-mono text-xs">{ev.event}</td>
                  <td className="py-3 px-4 tabular-nums">{ev.attempts ?? 0}</td>
                  <td className="py-3 px-4 text-zinc-600">{new Date(ev.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>

        {open && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setOpen(null)}
            data-testid="we-detail-modal"
          >
            <div
              className="bg-white rounded-sm border border-zinc-200 max-w-2xl w-full max-h-[80vh] overflow-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Webhook event</div>
                <div className="font-display text-xl tracking-tight font-medium">{open.event}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500 mb-1">Payload</div>
                <pre data-testid="we-payload" className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs font-mono overflow-auto">
{JSON.stringify(open.payload || {}, null, 2)}
                </pre>
              </div>
              <div className="mt-5 text-xs text-zinc-500">
                Replay is admin-controlled. Contact support to manually replay a failed event.
              </div>
            </div>
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}
